import i18n from 'i18next'

/**
 * Server errors in the interface language (docs/plans/localization.md, "Server messages").
 *
 * The API sends `message` in English and, for the errors it knows, a stable `code`
 * (`{ code: 'OTP_INVALID', message: 'Invalid OTP!' }`, with `params` when the text has
 * placeholders). A known code shows `errors:<code>`; otherwise the server's own message; otherwise
 * the fallback.
 *
 * `source` can be an axios error, an axios response, or a response body the caller already
 * unwrapped (several actions return `err.response.data`).
 */

export interface ApiErrorBody {
  code?: unknown
  message?: unknown
  params?: unknown
}

const isObject = (value: unknown): value is Record<string, any> => !!value && typeof value === 'object'

/** The response body behind an axios error or response, or the body itself. */
export const apiErrorBody = (source: unknown): ApiErrorBody | undefined => {
  if (!isObject(source)) return undefined
  // An error (axios or not): only its response body counts, never the transport message.
  if (source instanceof Error || 'isAxiosError' in source || 'response' in source) {
    const data = (source as { response?: { data?: unknown } }).response?.data
    return isObject(data) ? data : undefined
  }
  // An axios response.
  if ('config' in source && 'headers' in source && 'data' in source) {
    return isObject(source.data) ? source.data : undefined
  }
  return source
}

/** The error code the server sent, when it is one the web knows. */
export const apiErrorCode = (source: unknown): string | undefined => {
  const code = apiErrorBody(source)?.code
  return typeof code === 'string' && i18n.exists(`errors:${code}`) ? code : undefined
}

/** The message to show for a server error, or undefined when the server sent neither code nor text. */
export const apiErrorText = (source: unknown): string | undefined => {
  const body = apiErrorBody(source)
  if (!body) return undefined
  const code = apiErrorCode(body)
  if (code) return String(i18n.t(`errors:${code}`, isObject(body.params) ? body.params : {}))
  const message = Array.isArray(body.message) ? body.message[0] : body.message
  return typeof message === 'string' && message.trim() ? message : undefined
}

/**
 * `apiErrorText`, or the fallback translation key (`'auth:login.errors.loginFailed'`) when the
 * server sent nothing to show.
 */
export const apiErrorMessage = (source: unknown, fallbackKey: string): string =>
  apiErrorText(source) ?? String(i18n.t(fallbackKey))
